"use client";

import * as React from "react";
import { useState } from "react";
import {
  Settings,
  Gift,
  ConciergeBell,
  LucideIcon,
  CircleDollarSign,
  ChartSpline,
  History,
  CircleUserRound,
  House,
  Workflow,
  SquareMenu,
  Blocks,
  SparkleIcon,
  HandCoins,
  Archive,
} from "lucide-react";

import { NavMain } from "@/components/nav-main";
import {
  Sidebar,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarContent,
  SidebarHeader,
} from "@/components/ui/sidebar";
import cloneDeep from "lodash/cloneDeep";
import Link from "next/link";
import { useTheme } from "next-themes";
import LogoutButton from "@/components/logoutbutton";
import { useGlobal } from "@/app/contexts/globalcontext";
import { useTranslations } from "@/hooks/useTranslations";
import Image from "next/image";
import defaultProfile from "@/public/image/defaultProfile.jpg";
import { localUrl } from "@/api/api";
import type { StaticImageData } from "next/image";

// Type definitions
interface NavItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  isAuth?: boolean;
  items?: NavItem[];
  isAbsolute?: boolean;
  className?: string;
  onClick?: () => void;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

interface SidebarData {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  navMain: NavGroup[];
}

interface PlanFeatures {
  allow_point_collection?: number;
  allow_stamp_card?: number;
  allow_gift_voucher?: number;
  allow_membership?: number;
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  planFeatures?: PlanFeatures;
  addonTypeId?: number | null;
  showInteractiveAds?: boolean;
  isMainBranch?: boolean;
  userPermissions?: string[] | null;
  userRoleName?: string | null;
}

export function AppSidebar({
  planFeatures,
  addonTypeId,
  showInteractiveAds = true,
  isMainBranch = false,
  userPermissions = null,
  userRoleName = null,
  ...props
}: AppSidebarProps) {
  const { t } = useTranslations("translation");
  const [sidebarError, setSidebarError] = useState<string | null>(null);

  // Dynamic sidebar data based on locale
  const data: SidebarData = {
    user: {
      name: "shadcn",
      email: "m@example.com",
      avatar: "/avatars/shadcn.jpg",
    },
    navMain: [
      {
        title: t("merchant"),
        items: [
          {
            title: t("dashboard"),
            icon: ChartSpline,
            url: "/dashboard",
            items: [
              {
                title: t("dashboard"),
                url: "/dashboard/landing",
              },
              {
                title: t("analyticalDashboard"),
                url: "/dashboard/analytical-dashboard",
              },
              {
                title: t("report"),
                url: "/dashboard/report",
              },
            ],
          },
          {
            title: t("payableHistory"),
            url: "/payableHistory",
            icon: HandCoins,
          },
          {
            title: t("transitionHistory"),
            url: "/transition_history",
            icon: History,
          },
          {
            title: t("customer"),
            url: "/customer",
            icon: CircleUserRound,
            items: [
              {
                title: t("customers"),
                url: "/customer/customerManagement",
              },
              {
                title: t("management"),
                url: "/customer/category",
              },
              {
                title: t("customerManagement"),
                url: "/customer/customerManagement",
              },
              {
                title: t("collectExchange"),
                url: "/customer/collectExchange",
              },
              {
                title: t("notification"),
                url: "/customer/notification",
              },
              {
                title: t("email"),
                url: "/customer/email",
              },
            ],
          },
          {
            title: t("merchants"),
            url: "/merchant",
            icon: House,
            items: [
              {
                title: t("profile"),
                url: "/merchant/profile",
              },
              {
                title: t("gallery"),
                url: "/merchant/gallery",
              },
              {
                title: t("branches"),
                url: "/merchant/branches",
              },
              {
                title: t("users"),
                url: "/merchant/users",
              },
              {
                title: t("menu"),
                url: "/merchant/menu",
                items: [
                  {
                    title: t("menu"),
                    url: "/merchant/menu",
                  },
                  {
                    title: t("category"),
                    url: "/merchant/menu/category",
                    icon: SquareMenu,
                  },
                  {
                    title: t("options"),
                    url: "/merchant/menu/options",
                  },
                  {
                    title: t("discount"),
                    url: "/merchant/menu/discount",
                  },
                  {
                    title: t("taxes"),
                    url: "/merchant/menu/taxes",
                  },
                ],
              },
            ],
          },
          {
            title: t("connect"),
            url: "/connect",
            icon: Workflow,
            items: [
              {
                title: t("posts"),
                url: "/connect",
              },
            ],
          },
          {
            title: t("rewards"),
            url: "/rewards",
            icon: Gift,
            items: [
              {
                title: t("stampPoints"),
                url: "/rewards/stampPoints",
                items: [
                  {
                    title: t("request"),
                    url: "/rewards/stampPoints",
                  },
                  {
                    title: t("exchangeableItems"),
                    url: "/rewards/stampPoints/exchangeItems",
                  },
                ],
              },
              {
                title: t("cards"),
                url: "/rewards/cards",
                items: [],
              },
              {
                title: t("promotionOffers"),
                url: "/rewards/promotionOffers",
                items: [],
              },
              {
                title: t("interactiveAds"),
                url: "/rewards/interactiveAds",
                items: [],
              },
            ],
          },
          {
            title: t("orderReservations"),
            url: "/orderReservations",
            icon: ConciergeBell,
          },
          {
            title: t("archive"),
            url: "/archive",
            icon: Archive,
          },
          {
            title: t("billingPayments"),
            url: "/billingPayments",
            icon: CircleDollarSign,
            items: [
              {
                title: t("paymentMethods"),
                url: "/billingPayments/paymentMethods",
              },
              {
                title: t("paymentTransitions"),
                url: "/billingPayments/paymentTransitions",
              },
              {
                title: t("paymentSubscriptions"),
                url: "/billingPayments/plansSubscriptions",
              },
            ],
          },

          {
            title: t("planAndFeature") || "Plans and Features",
            url: "/plan",
            icon: SparkleIcon,
          },
        ],
      },
      {
        title: t("extensions"),
        items: [
          {
            title: t("extension"),
            url: "/extensions",
            icon: Blocks,
          },
        ],
      },
      {
        title: t("settings"),
        items: [
          {
            title: t("setting"),
            url: "/settings",
            icon: Settings,
            items: [
              {
                title: t("rolePermission"),
                url: "/settings/rolePermission",
              },
              {
                title: t("printSetup"),
                url: "/settings/printSetup",
              },
            ],
          },
        ],
      },
    ],
  };

  const filteredNavMain = React.useMemo(() => {
    if (!data?.navMain) return [];
    const navMain = cloneDeep(data.navMain);

    const normalizedRole = userRoleName
      ? userRoleName.toLowerCase().replace(/\s+/g, "")
      : "";
    const isSuperAdmin = normalizedRole === "superadmin";
    const permSet = Array.isArray(userPermissions)
      ? new Set(userPermissions)
      : null;

    const hasPerm = (...names: string[]): boolean => {
      if (isSuperAdmin || !permSet) return true;
      return names.some((name) => permSet.has(name));
    };

    const merchantGroup = navMain.find(
      (group) => group.title === t("merchant"),
    );

    if (merchantGroup && Array.isArray(merchantGroup.items)) {
      // 1. Dashboard (/dashboard) -> dashboard.oversight, report.oversight
      const dashboardItem = merchantGroup.items.find(
        (item) => item.url === "/dashboard",
      );
      if (dashboardItem && Array.isArray(dashboardItem.items)) {
        dashboardItem.items = dashboardItem.items.filter((sub) => {
          if (
            sub.url === "/dashboard/landing" ||
            sub.url === "/dashboard/analytical-dashboard"
          ) {
            return hasPerm("dashboard.oversight");
          }
          if (sub.url === "/dashboard/report") {
            return hasPerm("report.oversight");
          }
          return true;
        });
      }

      // 2. Customer (/customer) -> customerManagement.manage, notification.manage, emailMarketing.manage
      const customerItem = merchantGroup.items.find(
        (item) => item.url === "/customer",
      );
      if (customerItem && Array.isArray(customerItem.items)) {
        customerItem.items = customerItem.items.filter((sub) => {
          if (
            sub.url === "/customer/customerManagement" ||
            sub.url === "/customer/category" ||
            sub.url === "/customer/collectExchange"
          ) {
            return hasPerm("customerManagement.manage");
          }
          if (sub.url === "/customer/notification") {
            return hasPerm("notification.manage");
          }
          if (sub.url === "/customer/email") {
            return hasPerm("emailMarketing.manage");
          }
          return true;
        });
      }

      // 3. Merchants (/merchant) -> branchManagement.manage, userManagement.manage, menu.social / setup.order
      const merchantsItem = merchantGroup.items.find(
        (item) => item.url === "/merchant",
      );
      if (merchantsItem && Array.isArray(merchantsItem.items)) {
        merchantsItem.items = merchantsItem.items.filter((sub) => {
          if (sub.url === "/merchant/branches") {
            return hasPerm("branchManagement.manage");
          }
          if (sub.url === "/merchant/users") {
            return hasPerm("userManagement.manage");
          }
          if (sub.url === "/merchant/menu") {
            return hasPerm("menu.social", "setup.order");
          }
          return true;
        });
      }

      // 4. Connect (/connect) -> post.social
      const connectItem = merchantGroup.items.find(
        (item) => item.url === "/connect",
      );
      if (connectItem && Array.isArray(connectItem.items)) {
        connectItem.items = connectItem.items.filter((sub) => {
          if (sub.url === "/connect") {
            return hasPerm("post.social");
          }
          return true;
        });
      }

      // 5. Rewards (/rewards) -> rewardsRequest.social, interactiveAds.social (+ addon checks)
      const rewardsItem = merchantGroup.items.find(
        (item) => item.url === "/rewards" || item.title === t("rewards"),
      );
      if (rewardsItem && Array.isArray(rewardsItem.items)) {
        const showStampPoints = addonTypeId === 5 || addonTypeId === 6;

        rewardsItem.items = rewardsItem.items.filter((sub) => {
          if (
            sub.url === "/rewards/stampPoints" ||
            sub.title === "Stamp/ Points"
          ) {
            return showStampPoints && hasPerm("rewardsRequest.social");
          }
          if (
            sub.url === "/rewards/cards" ||
            sub.url === "/rewards/promotionOffers"
          ) {
            return hasPerm("rewardsRequest.social");
          }
          if (
            sub.url === "/rewards/interactiveAds" ||
            sub.title === t("interactiveAds")
          ) {
            return showInteractiveAds && hasPerm("interactiveAds.social");
          }
          return true;
        });
      }

      // 6. Billing & Payments (/billingPayments) -> billingAndPayment.oversight, paymentInfo.itAdmin
      const billingItem = merchantGroup.items.find(
        (item) => item.url === "/billingPayments",
      );
      if (billingItem && Array.isArray(billingItem.items)) {
        billingItem.items = billingItem.items.filter((sub) => {
          if (sub.url === "/billingPayments/paymentMethods") {
            return hasPerm("billingAndPayment.oversight", "paymentInfo.itAdmin");
          }
          if (
            sub.url === "/billingPayments/paymentTransitions" ||
            sub.url === "/billingPayments/plansSubscriptions"
          ) {
            return hasPerm("billingAndPayment.oversight");
          }
          return true;
        });
      }

      // Filter top-level items inside Merchant group
      merchantGroup.items = merchantGroup.items.filter((item) => {
        if (item.url === "/transition_history") {
          return hasPerm("transactionHistory.oversight");
        }
        if (item.url === "/orderReservations") {
          return hasPerm(
            "order.order",
            "reservation.order",
            "tracking.order",
            "cancellation.order",
          );
        }
        if (item.url === "/archive") {
          return hasPerm("archiveOrDeactivate.itAdmin");
        }
        if (item.url === "/plan") {
          return hasPerm("planFeaturesUpgrade.itAdmin");
        }
        if (
          item.url === "/dashboard" ||
          item.url === "/customer" ||
          item.url === "/merchant" ||
          item.url === "/connect" ||
          item.url === "/rewards" ||
          item.url === "/billingPayments"
        ) {
          return Array.isArray(item.items) && item.items.length > 0;
        }
        return true;
      });
    }

    // 7. Settings (/settings) -> generalSetting.itAdmin (+ isMainBranch for Role & Permission)
    const settingsGroup = navMain.find(
      (group) => group.title === t("settings"),
    );
    if (settingsGroup && Array.isArray(settingsGroup.items)) {
      if (!hasPerm("generalSetting.itAdmin")) {
        settingsGroup.items = [];
      } else {
        const settingItem = settingsGroup.items.find(
          (item) => item.url === "/settings" || item.title === t("setting"),
        );
        if (settingItem && settingItem.items && !isMainBranch) {
          settingItem.items = settingItem.items.filter(
            (item) =>
              item.url !== "/settings/rolePermission" &&
              item.title !== t("rolePermission"),
          );
        }
      }
    }

    return navMain.filter(
      (group) => Array.isArray(group.items) && group.items.length > 0,
    );
  }, [
    t,
    planFeatures,
    addonTypeId,
    showInteractiveAds,
    isMainBranch,
    userPermissions,
    userRoleName,
    data?.navMain,
  ]);

  const { theme } = useTheme();
  const { userData } = useGlobal();

  function getProfileImageUrl(
    profile: string | undefined,
  ): string | StaticImageData {
    if (!profile) return defaultProfile;
    const cleanProfile = profile.replace(/^\/+/, "").replace(/^storage\//, "");
    return `${localUrl}/storage/${cleanProfile}`;
  }

  function formatPhone(phone: string): string {
    const digits = phone.replace(/\D/g, ""); // strip non-digits

    if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3");
    }

    if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
    }

    return digits;
  }

  return (
    <Sidebar
      className={`overflow-x-hidde dark:bg-black bg-gray-800 ${
        theme === "dark"
          ? "bg-sidebar"
          : "min-h-screen bg-white shadow-xl"
      }`}
      collapsible="icon"
      {...props}
    >
      <SidebarHeader
        className={`border-b ${
          theme === "dark" ? "border-sidebar-border" : "border-gray-200"
        }`}
      >
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="">
              <Link href={`/merchant/users/detail/${userData?.id}`}>
                <div
                  className={`flex aspect-square size-8 items-center justify-center rounded-lg ${
                    theme === "dark"
                      ? "bg-sidebar-primary text-sidebar-primary-foreground"
                      : " text-white"
                  } shrink-0`}
                >
                  <div className="flex-shrink-0 w-10 h-10">
                    <Image
                      src={getProfileImageUrl(userData?.profile as string)}
                      alt={"user profile img"}
                      width={50}
                      height={50}
                      className="rounded-full object-cover border border-gray-500 w-full h-full"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-0.5 px-3 leading-none justify-center min-w-0 flex-1">
                  <div
                    className="flex items-center justify-between gap-3"
                    id="profile-info"
                  >
                    <div className="flex flex-col flex-1 min-w-0 space-y-1">
                      <span className=" font-medium truncate dark:text-white text-gray-900">
                        {String(
                          userData?.username ||
                            userData?.slug ||
                            userData?.name ||
                            "User",
                        )}
                      </span>
                      <span className=" text-gray-500 dark:text-gray-400 truncate">
                        {userData?.phone
                          ? formatPhone(userData.phone as string)
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="overflow-y-auto overflow-x-hidden">
        {filteredNavMain && <NavMain items={filteredNavMain} />}

        <LogoutButton />
      </SidebarContent>

      {/* Red Bottom Line Error */}
      {sidebarError && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-600 text-white py-3 px-6 flex items-center justify-between shadow-lg z-50">
          <span className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {sidebarError}
          </span>
          <button
            onClick={() => setSidebarError(null)}
            className="text-white hover:text-red-200 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}
    </Sidebar>
  );
}
